import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Divider,
  Avatar,
  Box,
  Typography,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Split, MapPin } from "lucide-react";
import { Autocomplete, LoadScriptNext } from "@react-google-maps/api";

const SplitLegModal = ({ open, handleClose, handleSubmit, isSubmitting = false }) => {
  const [addressData, setAddressData] = useState({
    newCompanyName: "",
    address: "",
    address2: "",
    city: "",
    state: "",
    postal: "",
    country: "",
    latitude: "",
    longitude: "",
  });

  useEffect(() => {
    if (!open) {
      // Reset form when modal closes
      setAddressData({
        newCompanyName: "",
        address: "",
        address2: "",
        city: "",
        state: "",
        postal: "",
        country: "",
        latitude: "",
        longitude: "",
      });
    }
  }, [open]);

  const handlePlaceChanged = (place) => {
    if (place.address_components) {
      const address = {
        newCompanyName: place.name || "",
        address: "",
        address2: "",
        city: "",
        state: "",
        postal: "",
        country: "",
        latitude: place.geometry?.location.lat() || "",
        longitude: place.geometry?.location.lng() || "",
      };

      let streetNumber = "";
      let route = "";
      let subpremise = "";
      let premise = "";

      place.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("street_number")) {
          streetNumber = component.long_name;
        } else if (types.includes("route")) {
          route = component.long_name;
        } else if (types.includes("subpremise")) {
          subpremise = component.long_name;
        } else if (types.includes("premise")) {
          premise = component.long_name;
        } else if (types.includes("locality")) {
          address.city = component.long_name;
        } else if (types.includes("administrative_area_level_1")) {
          address.state = component.long_name;
        } else if (types.includes("postal_code")) {
          address.postal = component.long_name;
        } else if (types.includes("country")) {
          address.country = component.long_name;
        }
      });

      // Combine address components for address1 and address2
      const fullStreetAddress = `${streetNumber} ${route}`.trim();
      address.address = fullStreetAddress;

      // Use premise/subpremise for address2 if available
      if (subpremise || premise) {
        address.address2 = `${subpremise} ${premise}`.trim();
      } else {
        address.address2 = "";
      }

      setAddressData(address);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddressData((prev) => ({ ...prev, [name]: value }));
  };

  const onSubmit = () => {
    handleSubmit(addressData);
  };

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? undefined : handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          overflow: "hidden",
        },
      }}
    >
      <DialogTitle
        sx={{
          bgcolor: "primary.main",
          color: "primary.contrastText",
          py: 2,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
        }}
      >
        <Avatar
          sx={{
            bgcolor: "primary.contrastText",
            color: "primary.main",
            width: 32,
            height: 32,
          }}
        >
          <Split size={18} />
        </Avatar>
        <Typography variant="h6" component="div" sx={{ fontSize: "1.1rem" }}>
          Split Leg
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <LoadScriptNext
          googleMapsApiKey={import.meta.env.VITE_API_MAP_KEY}
          libraries={["places"]}
        >
          <Paper sx={{ p: 1.5, mb: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <MapPin size={18} color="text.secondary" />
                  <Typography variant="body2" color="text.secondary">
                    Search for the split location
                  </Typography>
                </Box>
                <Autocomplete
                  onLoad={(autocomplete) => {
                    window.autocompleteSplitLegModalRef = autocomplete;
                  }}
                  onPlaceChanged={() => {
                    if (window.autocompleteSplitLegModalRef) {
                      const place =
                        window.autocompleteSplitLegModalRef.getPlace();
                      handlePlaceChanged(place);
                    }
                  }}
                >
                  <TextField
                    fullWidth
                    placeholder="Search Address"
                    variant="outlined"
                    size="small"
                  />
                </Autocomplete>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 2 }} />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Company name"
                  name="newCompanyName"
                  value={addressData.newCompanyName}
                  onChange={handleChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Address"
                  name="address"
                  value={addressData.address}
                  onChange={handleChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ autoComplete: "off" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Address 2"
                  name="address2"
                  value={addressData.address2}
                  onChange={handleChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="City"
                  name="city"
                  value={addressData.city}
                  onChange={handleChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="State"
                  name="state"
                  value={addressData.state}
                  onChange={handleChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ autoComplete: "off" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Postal Code"
                  name="postal"
                  value={addressData.postal}
                  onChange={handleChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ autoComplete: "off" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Country"
                  name="country"
                  value={addressData.country}
                  onChange={handleChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ autoComplete: "off" }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Latitude"
                  name="latitude"
                  value={addressData.latitude}
                  onChange={handleChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  variant="outlined"
                  fullWidth
                  label="Longitude"
                  name="longitude"
                  value={addressData.longitude}
                  onChange={handleChange}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                  disabled
                />
              </Grid>
            </Grid>
          </Paper>
        </LoadScriptNext>
      </DialogContent>
      <DialogActions sx={{ p: 2.5, bgcolor: "grey.50" }}>
        <Button
          onClick={handleClose}
          size="medium"
          color="error"
          disabled={isSubmitting}
        >
          Cancel
        </Button>
        <Button
          onClick={onSubmit}
          variant="contained"
          size="medium"
          disabled={isSubmitting}
          sx={{
            px: 3,
            py: 0.75,
            minWidth: 100,
          }}
        >
          {isSubmitting ? <CircularProgress size={22} color="inherit" /> : "Split Leg"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SplitLegModal;
